import { useEffect, useState } from 'react';
import { X, PlayCircle, ShieldCheck, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { provisioningAPI } from '../services/api.js';
import { ReadinessBadge, ReadinessChecks } from './LineReadiness.jsx';
import VoicePicker from './VoicePicker.jsx';

// ADD NUMBER — the provisioning action behind `POST /admin/provision-line`.
//
// This is the one endpoint in the platform that mutates Telnyx AND LiveKit, so
// the whole flow is built around that:
//
//   • It ALWAYS dry-runs first. `confirm` defaults to false server-side and the
//     UI never sends true until the user has seen exactly what would change and
//     pressed a second, differently-labelled button.
//   • The trunk is chosen EXPLICITLY and the production one is labelled. This is
//     not decoration: `trunks[0]` is the Grok TEST trunk, and defaulting to first
//     is how a production number gets wired into the test environment.
//   • `voiceProvider` and `voiceId` travel TOGETHER, always. A provider without
//     its matching id hands e.g. an ElevenLabs id to Deepgram and the line does
//     not synthesize. The server refuses to guess; so does this form.
//
// The result carries a `readiness` object computed from the live systems AFTER
// the run — so the screen proves the line works rather than claiming it does. It
// is the same shape the readiness column renders, so ReadinessChecks is reused.

const STATE_LABEL = {
  pending_telnyx: 'Stopped at Telnyx — the number is not pointed at the box yet',
  pending_trunk: 'Stopped at LiveKit — the number is not on a trunk yet',
  pending_voices: 'Stopped at voices — the tenant has no callable persona yet',
  active: 'All four steps complete'
};

const STEP_LABEL = {
  phone_number: 'Phone-number record',
  telnyx_connection: 'Telnyx → the box',
  livekit_trunk: 'LiveKit inbound trunk',
  panel_persona: 'Callable voice'
};

function StepRow({ s }) {
  const Icon = s.ok ? CheckCircle2 : AlertTriangle;
  const tone = s.ok ? 'text-emerald-300' : 'text-rose-300';
  return (
    <li className="flex gap-2 py-1.5 text-sm">
      <Icon size={15} className={`mt-0.5 shrink-0 ${tone}`} />
      <div className="min-w-0">
        <div className="text-stone-200">
          {STEP_LABEL[s.step] || s.step}
          {/* `changed` is the difference between "already correct" and "we altered
              a live system" — the single most important thing to show after a run. */}
          {s.changed && (
            <span className="ml-2 badge bg-amber-950/60 text-amber-300 text-[10px]">changed</span>
          )}
        </div>
        {s.detail && <div className="text-xs text-stone-500">{s.detail}</div>}
        {s.error && <div className="text-xs text-rose-300">{s.error}</div>}
      </div>
    </li>
  );
}

// Controlled: the PARENT owns the trigger button and decides when this is mounted.
// `onDone` is called on close, and tells the parent whether anything actually
// changed so it can reload only when it needs to.
export default function ProvisionLine({ tenantId, tenantName, onDone }) {
  const [targets, setTargets] = useState(null);
  const [did, setDid] = useState('');
  const [trunkId, setTrunkId] = useState('');
  const [voice, setVoice] = useState(null); // { voiceProvider, voiceId, name }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // dry-run result
  const [applied, setApplied] = useState(null); // confirmed result

  useEffect(() => {
    if (targets) return;
    provisioningAPI
      .targets()
      .then((r) => {
        const t = r?.data || r;
        setTargets(t);
        // Default to the PRODUCTION trunk, explicitly — never the first in the list.
        if (t?.productionTrunkId) setTrunkId(t.productionTrunkId);
      })
      .catch((err) => setError(err.message || 'Could not load provisioning targets.'));
  }, [targets]);

  const reset = () => {
    setPreview(null);
    setApplied(null);
    setError('');
  };

  const run = async (confirm) => {
    setBusy(true);
    setError('');
    try {
      const payload = { tenantId, did: did.trim(), confirm };
      if (trunkId) payload.trunkId = trunkId;
      // Provider and id together or not at all.
      if (voice?.voiceId && voice?.voiceProvider) payload.voice = voice;
      const r = await provisioningAPI.provision(payload);
      const result = r?.data || r;
      if (confirm) setApplied(result);
      else setPreview(result);
    } catch (err) {
      setError(err.body?.message || err.message || 'Provisioning call failed.');
    } finally {
      setBusy(false);
    }
  };

  const close = () => onDone?.(!!applied);

  const result = applied || preview;
  const selectedTrunk = targets?.trunks?.find((t) => t.id === trunkId);

  return (
    <div className="rounded-lg border border-alfred-gold/30 bg-stone-950/40 p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg text-stone-100">Add a phone number</h3>
          <p className="mt-1 text-sm text-stone-400">
            Points the number at the box, puts it on a LiveKit trunk, and makes sure the account has
            a voice that can answer{tenantName ? ` — for ${tenantName}` : ''}.
          </p>
        </div>
        <button type="button" className="btn-ghost" onClick={close}>
          <X size={14} /> Close
        </button>
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <div className="space-y-4">
        <div>
          <label className="field-label">Phone number</label>
          <input
            className="field-input"
            placeholder="+18652730436"
            value={did}
            onChange={(e) => { setDid(e.target.value); reset(); }}
            disabled={busy || !!applied}
          />
          <p className="mt-1 text-xs text-stone-500">
            The number must already exist in Telnyx. This points an existing number at the platform;
            it does not buy one.
          </p>
        </div>

        <div>
          <label className="field-label">LiveKit trunk</label>
          <select
            className="field-input"
            value={trunkId}
            onChange={(e) => { setTrunkId(e.target.value); reset(); }}
            disabled={busy || !!applied}
          >
            {(targets?.trunks || []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} {t.isProduction ? '— PRODUCTION' : ''} ({t.numberCount} number
                {t.numberCount === 1 ? '' : 's'}
                {t.wildcard ? ', wildcard' : ''})
              </option>
            ))}
          </select>
          {/* The trap, stated on screen rather than left to lore. */}
          {selectedTrunk && !selectedTrunk.isProduction && (
            <p className="mt-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
              This is <strong>not</strong> the production trunk. A number on a test trunk is answered
              by a different agent deployment — the account&rsquo;s own voice and prompt will not be
              what callers hear.
            </p>
          )}
          {targets?.trunksError && (
            <p className="mt-2 text-xs text-amber-300">
              Couldn&rsquo;t reach LiveKit to list trunks — {targets.trunksError}
            </p>
          )}
        </div>

        <div>
          <label className="field-label">Voice (only needed if this account has no voices yet)</label>
          {/* VoicePicker returns provider + id together, which is the whole point. */}
          <VoicePicker
            provider={voice?.voiceProvider || ''}
            voiceId={voice?.voiceId || ''}
            onChange={({ provider, voiceId }) => {
              // Provider and id are set from ONE selection, so they cannot drift
              // apart — which is the failure this field exists to avoid.
              setVoice(voiceId ? { voiceProvider: provider, voiceId } : null);
              reset();
            }}
          />
          <p className="mt-1 text-xs text-stone-500">
            Leave blank when the account already has a persona. Provider and voice are always saved
            together — a provider without its matching voice does not synthesize.
          </p>
        </div>
      </div>

      {/* ── The run ─────────────────────────────────────────────────────────── */}
      <div className="mt-5 flex flex-wrap gap-2">
        {!applied && (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => run(false)}
            disabled={busy || !did.trim()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            Check what this would do
          </button>
        )}
        {preview && !applied && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => run(true)}
            disabled={busy}
          >
            <ShieldCheck size={14} /> Apply these changes
          </button>
        )}
      </div>

      {preview && !applied && (
        <p className="mt-2 text-xs text-stone-400">
          Nothing has been changed yet — this is a preview. Telnyx and LiveKit are only touched when
          you press Apply.
        </p>
      )}

      {result && (
        <div className="mt-5 rounded-lg border border-stone-800 bg-stone-950/60 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-stone-200">
              {applied ? 'Result' : 'Preview'} — {STATE_LABEL[result.state] || result.state}
            </span>
            {result.readiness && <ReadinessBadge status={result.readiness.status} />}
          </div>

          <ul className="mt-3 divide-y divide-stone-900">
            {(result.steps || []).map((s) => (
              <StepRow key={s.step} s={s} />
            ))}
          </ul>

          {/* Prove it rather than claim it: this readiness is recomputed from the
              live systems after the run, not inferred from the steps above. */}
          {result.readiness && (
            <div className="mt-4 border-t border-stone-800 pt-3">
              <div className="mb-2 text-xs uppercase tracking-wide text-stone-500">
                {applied ? 'Checked against the live systems afterwards' : 'How the line looks right now'}
              </div>
              <ReadinessChecks checks={result.readiness.checks} did={result.did} />
            </div>
          )}

          {applied && result.state !== 'active' && (
            <p className="mt-3 text-sm text-amber-300">
              Partly provisioned. Fix the step above and run this again — it is safe to re-run and
              picks up where it stopped.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
